import {
  BASE_URL,
  BASE_URL_API,
  SENSORS_URL,
  OTA_URL,
  AI_RAG_CHAT_HISTORY_URL,
  AI_RAG_CHAT_NEW_URL,
  AI_RAG_CHAT_URL,
  AI_RAG_CHAT_VOICE_URL,
  AI_RAG_CHAT_VOICE_STREAM_URL,
  AI_VOICE_PROCESS_URL,
  AI_VOICE_SESSION_CONTEXT_URL,
  AI_VOICE_SESSION_RESET_URL,
} from "../config/env";

const trimLeadingSlash = (value) => String(value || "").replace(/^\/+/, "");
const buildUrl = (baseUrl, path = "") => {
  const normalizedPath = trimLeadingSlash(path);
  return normalizedPath ? `${baseUrl}/${normalizedPath}` : baseUrl;
};
const withQuery = (url, params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    searchParams.set(key, String(value));
  });

  const query = searchParams.toString();
  return query ? `${url}?${query}` : url;
};

export const apiUrl = (path = "") => buildUrl(BASE_URL_API, path);
export const appUrl = (path = "") => buildUrl(BASE_URL, path);
export const sensorsUrl = (path = "") => buildUrl(SENSORS_URL, path);
export const otaUrl = (path = "") => buildUrl(OTA_URL, path);

export const endpoints = {
  crud: {
    delete: (type, id) => apiUrl(`${type}/delete/${id}`),
    deleteMany: (type) => apiUrl(`${type}/delete`),
  },
  auth: {
    login: () => appUrl("login/auth"),
    logout: () => appUrl("login/logout"),
    signup: () => appUrl("auth/signup"),
    verifyCode: () => appUrl("auth/verify-code"),
    forgotPassword: () => appUrl("auth/forgot-password"),
    verifyResetCode: () => appUrl("auth/verify-reset-code"),
    resetPassword: () => appUrl("auth/reset-password"),
    invitePassword: () => appUrl("auth/invite-password"),
    acceptInvitePassword: () => appUrl("auth/accept-invite-password"),
    contact: () => appUrl("auth/contact"),
    me: () => apiUrl("user/me"),
  },
  user: {
    get: (id) => apiUrl(`user/get/${id}`),
    update: (id) => apiUrl(`user/update/${id}`),
    delete: (id) => apiUrl(`user/delete/${id}`),
    deleteMany: () => apiUrl("user/delete"),
    saveSettings: (id) => apiUrl(`user/save-settings/${id}`),
    aiChatVoicePreference: () => apiUrl("user/me/ai-chat-voice"),
    settings: () => apiUrl("user/settings"),
    fcmToken: () => apiUrl("user/fcm-token"),
    list: () => apiUrl("user/list"),
    filtered: (orgId) => apiUrl(`user/filtered/${orgId}`),
    create: () => apiUrl("user/create"),
  },
  organization: {
    get: (id) => apiUrl(`organization/get/${id}`),
    list: () => apiUrl("organization/list"),
    filtered: (orgId) => apiUrl(`organization/filtered/${orgId}`),
  },
  location: {
    deleteMany: () => apiUrl("location/delete"),
  },
  device: {
    get: (id) => apiUrl(`device/get/${id}`),
    getByDid: (did) => apiUrl(`device/get-did/${did}`),
    list: () => apiUrl("device/list"),
    filtered: (orgId) => apiUrl(`device/filtered/${orgId}`),
    filteredSse: (orgId, params = {}) => withQuery(apiUrl(`device/filtered-sse/${orgId}`), params),
    getSse: (id) => apiUrl(`device/get-sse/${id}`),
    delete: (id) => apiUrl(`device/delete/${id}`),
    deleteMany: () => apiUrl("device/delete"),
    deleteImpact: () => apiUrl("device/delete-impact"),
    planAction: (id) => apiUrl(`device/plan-action/${id}`),
    planRequest: (id) => apiUrl(`device/plan-request/${id}`),
    planExpiry: () => apiUrl("device/plan-expiry"),
    planNotifications: () => apiUrl("device/plan-notifications"),
    readPlanNotification: (notificationId) =>
      apiUrl(`device/plan-notifications/read/${notificationId}`),
    statusLogs: (id, query = "") =>
      query ? `${apiUrl(`device/status-logs/${id}`)}?${query}` : apiUrl(`device/status-logs/${id}`),
    datastreamLogs: (statusLogId) => apiUrl(`device/datastream-logs/${statusLogId}`),
  },
  template: {
    get: (id) => apiUrl(`template/get/${id}`),
    list: () => apiUrl("template/list"),
    examplesList: () => apiUrl("template/examples/list"),
    examplesGet: (id) => apiUrl(`template/examples/get/${id}`),
  },
  dashboard: {
    get: (id) => apiUrl(`dashboard/get/${id}`),
    create: () => apiUrl("dashboard/create"),
    filtered: (orgId) => apiUrl(`dashboard/filtered/${orgId}`),
    examplesList: () => apiUrl("dashboard/examples/list"),
  },
  file: {
    upload: () => apiUrl("file/upload"),
    delete: () => apiUrl("file/delete"),
    deleteOne: (fileName) => apiUrl(`file/delete/${fileName}`),
  },
  data: {
    delete: () => apiUrl("data/delete"),
    queryEvents: (params) => withQuery(apiUrl("data/query_events"), params),
    notifications: (orgId, params = {}) => withQuery(apiUrl(`data/notifications/${orgId}`), params),
    notificationsSse: (orgId, params = {}) => withQuery(apiUrl(`data/notifications-sse/${orgId}`), params),
    notificationDetails: (orgId, notificationId, params = {}) =>
      withQuery(apiUrl(`data/notifications/${orgId}/details/${notificationId}`), params),
    weather: (params = {}) => withQuery(apiUrl("data/weather"), params),
    deleteNotification: (orgId) => apiUrl(`data/notifications/${orgId}`),
    clearNotifications: (orgId) => apiUrl(`data/notifications/${orgId}/all`),
    downloadReport: () => apiUrl("data/download_report"),
    downloadDashboardReport: () => apiUrl("data/download_dashboard_report"),
  },
  assetCard: {
    root: () => apiUrl("asset-card"),
    get: (id) => apiUrl(`asset-card/${id}`),
    delete: (id) => apiUrl(`asset-card/${id}`),
  },
  automation: {
    root: () => apiUrl("automation"),
    get: (id) => apiUrl(`automation/${id}`),
    filtered: (orgId) => apiUrl(`automation/filtered/${orgId}`),
    delete: (id) => apiUrl(`automation/${id}`),
    deleteMany: () => apiUrl("automation/delete"),
    setEnabled: () => apiUrl("automation/set-enabled"),
  },
  subscriber: {
    root: () => apiUrl("subscriber"),
    get: (id) => apiUrl(`subscriber/${id}`),
    details: (id) => apiUrl(`subscriber/${id}/details`),
    delete: (id) => apiUrl(`subscriber/${id}`),
    deleteMany: () => apiUrl("subscriber/delete"),
  },
  role: {
    list: () => apiUrl("roles"),
    get: (id) => apiUrl(`roles/${id}`),
    delete: (id) => apiUrl(`roles/${id}`),
    deleteMany: () => apiUrl("roles/delete"),
  },
  permission: {
    update: (id) => apiUrl(`permission/update/${id}`),
    listWithRoles: () => apiUrl("permission/list_with_roles"),
    rolesBulkSet: () => apiUrl("permission-roles/bulk-set"),
  },
  configuration: {
    system: () => apiUrl("configuration/system"),
    public: () => apiUrl("configuration/public"),
    assetCardDeviceDetails: () => apiUrl("configuration/asset-card/device-details"),
  },
  auditLog: {
    list: () => apiUrl("audit-logs"),
  },
  aiVoice: {
    process: () => AI_VOICE_PROCESS_URL,
    resetSession: () => AI_VOICE_SESSION_RESET_URL,
    sessionContext: () => AI_VOICE_SESSION_CONTEXT_URL,
    chat: () => AI_RAG_CHAT_URL,
    chatVoice: () => AI_RAG_CHAT_VOICE_URL,
    chatVoiceStream: () => AI_RAG_CHAT_VOICE_STREAM_URL,
    chatHistory: () => AI_RAG_CHAT_HISTORY_URL,
    newChat: () => AI_RAG_CHAT_NEW_URL,
    deleteChatHistory: () => AI_RAG_CHAT_HISTORY_URL,
    resolveText: () => apiUrl("ai-voice/resolve-text"),
    mediaLibrary: () => apiUrl("ai-voice/media-library"),
    uploadMedia: () => apiUrl("ai-voice/media-library/upload"),
  },
  sensors: {
    control: () => sensorsUrl("control"),
    stream: (device) => withQuery(sensorsUrl("stream"), { device }),
    action: (actionType, device) => withQuery(sensorsUrl(actionType), { device }),
  },
  ota: {
    shipmentList: () => otaUrl("shipment/list"),
    shipmentGet: (id) => otaUrl(`shipment/get/${id}`),
    shipmentDelete: (id) => otaUrl(`shipment/delete/${id}`),
    shipmentDeleteList: () => otaUrl("shipment/delete-list"),
    shipmentOperation: (operation, id) => otaUrl(`shipment/${operation}/${id}`),
    statusList: (id) => otaUrl(`status/list/${id}`),
    fileDelete: () => otaUrl("file/delete"),
    fileDeleteOne: (fileName) => otaUrl(`file/delete/${fileName}`),
  },
  userHealth: {
    my: () => apiUrl("user-health/my"),
    byOrg: (orgId) => apiUrl(`user-health/by-org/${orgId}`),
    get: (id) => apiUrl(`user-health/get/${id}`),
    statsByOrg: (orgId) => apiUrl(`user-health/stats-by-org/${orgId}`),
  },
  medicalDevice: {
    list: () => apiUrl("medical-device/list"),
    create: () => apiUrl("medical-device/create"),
    startScan: () => apiUrl("medical-device/start-scan"),
    finishScan: () => apiUrl("medical-device/finish-scan"),
  },
  peopleCounter: {
    organizations: () => apiUrl("people-counter/organizations"),
    devices: () => apiUrl("people-counter/devices"),
    deviceOptions: () => apiUrl("people-counter/devices/options"),
    device: (id) => apiUrl(`people-counter/devices/${id}`),
    statisticsOne: () => apiUrl("people-counter/statistics/one"),
    statisticsMany: () => apiUrl("people-counter/statistics/many"),
    statisticsLatest: () => apiUrl("people-counter/statistics/latest"),
  },
};

export default endpoints;
